'use client';

import { useState } from 'react';
import { useAppRouter as useRouter } from '@/lib/navigation';
import { Skeleton } from '@/components/ui/skeleton';
import { LoadingStates } from '@/components/loading-states';
import { ProcessHistoryHistoricForm } from '@/components/history/process-history-historic-form';
import { ProcessHistoryTaskHeader } from '@/components/history/process-history-task-header';
import {
  findAdjacentProcessHistoryTasks,
  useProcessHistory,
} from '@/components/history/use-process-history';
import { decodeHistoryTaskRouteId } from '@/components/history/process-history-utils';
import {
  HISTORIC_FORBIDDEN_TITLE,
  HISTORIC_PROCESS_FORBIDDEN_MESSAGE,
  HISTORIC_TASK_FORBIDDEN_MESSAGE,
  useHistoricAccessDeniedRedirect,
} from '@/components/history/use-historic-access-denied-redirect';

interface HistoricTaskPageProps {
  params: {
    processInstanceId: string;
    taskId: string;
  };
}

export default function HistoricTaskPage({ params }: HistoricTaskPageProps) {
  const { processInstanceId, taskId: routeTaskId } = params;
  const taskId = decodeHistoryTaskRouteId(routeTaskId);
  const router = useRouter();
  const formScopeKey = `${processInstanceId}:${taskId}`;
  const [forbiddenFormScope, setForbiddenFormScope] = useState<string | null>(null);
  const formForbidden = forbiddenFormScope === formScopeKey;
  const { data, loading, error, forbidden } = useProcessHistory(processInstanceId);

  const taskItem = data?.tasks.find((item) => item.task.id === taskId) ?? null;
  const processDenied = !loading && forbidden;
  const taskDenied = Boolean(!loading && data && taskItem && !taskItem.canViewDetail);
  const accessDenied = processDenied || taskDenied || formForbidden;
  const accessDeniedMessage = processDenied
    ? HISTORIC_PROCESS_FORBIDDEN_MESSAGE
    : HISTORIC_TASK_FORBIDDEN_MESSAGE;

  const handleFormForbidden = () => {
    setForbiddenFormScope(formScopeKey);
  };

  useHistoricAccessDeniedRedirect(accessDenied, accessDeniedMessage);

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3 px-3 py-3">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (forbidden || formForbidden) {
    return (
      <div className="h-full" aria-live="polite">
        <span className="sr-only">{HISTORIC_FORBIDDEN_TITLE}</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-full items-center justify-center px-3 py-3">
        <LoadingStates error={error ?? 'No se pudo cargar el historial del caso.'} />
      </div>
    );
  }

  if (!taskItem) {
    return (
      <div className="flex h-full items-center justify-center px-3 py-3">
        <LoadingStates error="No se encontró la tarea solicitada en este caso." />
      </div>
    );
  }

  if (!taskItem.canViewDetail) {
    return (
      <div className="h-full" aria-live="polite">
        <span className="sr-only">{HISTORIC_FORBIDDEN_TITLE}</span>
      </div>
    );
  }

  const { previous, next } = findAdjacentProcessHistoryTasks(data.tasks, taskId);

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full max-w-none flex-col gap-4 px-3 py-3">
      <ProcessHistoryTaskHeader
        taskItem={taskItem}
        processInstanceId={processInstanceId}
        previous={previous}
        next={next}
        onNavigateTask={(nextTaskId) => {
          router.push(`/task-list/participated/${processInstanceId}/tasks/${nextTaskId}`);
        }}
      />

      <div className="min-h-0 flex-1 overflow-auto">
        <ProcessHistoryHistoricForm
          task={taskItem.task}
          formKey={taskItem.formKey}
          taskId={taskId}
          processInstanceId={processInstanceId}
          enabled={taskItem.canViewDetail}
          isStartForm={taskItem.isStartForm}
          onForbidden={handleFormForbidden}
        />
      </div>
    </div>
  );
}
